<template>
  <dashboard-layout title="Availability" :kicker="kickerLabel" :items="navItems">
    <div class="bg-white rounded-3 border shadow-sm p-3 p-md-4">
      <p class="inter-regular-family text-muted text-full-11 mb-3">
        Choose when you are available to work. Set at least one open day, and add time ranges for open days.
      </p>

      <div v-if="loadError" class="alert alert-warning py-2 px-3 small mb-3" role="alert">
        {{ loadError }}
      </div>

      <div v-if="loading" class="text-center py-5 inter-regular-family text-muted">
        Loading availability…
      </div>

      <form v-if="!loading" @submit.prevent="save">
        <div class="pa-grid">
          <div
            v-for="d in weekDays"
            :key="d.key"
            class="pa-row"
            :class="{ 'pa-row--off': !d.model.open }"
          >
            <label class="pa-day">
              <input v-model="d.model.open" type="checkbox">
              <span>{{ d.label }}</span>
            </label>

            <div class="pa-times">
              <input v-model="d.model.start" type="time" class="pa-input" :disabled="!d.model.open || d.model.fullDay">
              <input v-model="d.model.end" type="time" class="pa-input" :disabled="!d.model.open || d.model.fullDay">
            </div>

            <label class="pa-fullday">
              <input v-model="d.model.fullDay" type="checkbox" :disabled="!d.model.open">
              Full day
            </label>
          </div>
        </div>

        <button type="submit" class="pa-save inter-bold-family text-uppercase text-full-11 mt-3" :disabled="saving">
          {{ saving ? 'Saving…' : 'Save availability' }}
        </button>
      </form>
    </div>
  </dashboard-layout>
</template>

<script>
import DashboardLayout from './DashboardLayout.vue';
import { SALON_DASH_NAV, GROOMER_DASH_NAV } from '@/utils/dashboardNav';
import { readPartnerAuth, clearPartnerSession } from '@/utils/partnerProfileStorage';

function defaultAvailability() {
  const day = (open, fullDay, start, end) => ({ open, fullDay, start, end });
  return {
    monday: day(false, true, '09:00', '17:00'),
    tuesday: day(true, false, '09:00', '17:00'),
    wednesday: day(false, true, '09:00', '17:00'),
    thursday: day(false, true, '09:00', '17:00'),
    friday: day(true, false, '09:00', '17:00'),
    saturday: day(false, true, '09:00', '17:00'),
    sunday: day(true, false, '09:00', '17:00'),
  };
}

function mergeAvailability(saved) {
  const base = defaultAvailability();
  if (!saved || typeof saved !== 'object') return base;
  const out = {};
  Object.keys(base).forEach((k) => {
    out[k] = { ...base[k], ...(saved?.[k] || {}) };
  });
  return out;
}

export default {
  name: 'PartnerDashboardAvailability',
  components: { DashboardLayout },
  props: {
    partnerRole: {
      type: String,
      required: true,
      validator: (v) => v === 'salon' || v === 'groomer',
    },
  },
  data() {
    return {
      loading: true,
      saving: false,
      loadError: '',
      availability: defaultAvailability(),
    };
  },
  computed: {
    kickerLabel() {
      return this.partnerRole === 'salon' ? 'Salon dashboard' : 'Groomer dashboard';
    },
    navItems() {
      return this.partnerRole === 'salon' ? SALON_DASH_NAV : GROOMER_DASH_NAV;
    },
    weekDays() {
      const defs = [
        { key: 'monday', label: 'Monday' },
        { key: 'tuesday', label: 'Tuesday' },
        { key: 'wednesday', label: 'Wednesday' },
        { key: 'thursday', label: 'Thursday' },
        { key: 'friday', label: 'Friday' },
        { key: 'saturday', label: 'Saturday' },
        { key: 'sunday', label: 'Sunday' },
      ];
      return defs.map((d) => ({ ...d, model: this.availability[d.key] }));
    },
  },
  async mounted() {
    const auth = readPartnerAuth();
    if (!auth?.joinType || !auth.email || !auth.token) {
      this.$router.replace({ path: '/login' });
      return;
    }
    const jt = String(auth.joinType).toLowerCase();
    if (jt !== this.partnerRole) {
      this.$router.replace({ path: `/dashboard/${jt}` });
      return;
    }
    await this.load();
  },
  methods: {
    validate() {
      const days = this.availability || {};
      const anyOpen = Object.values(days).some((d) => d && d.open);
      if (!anyOpen) return false;
      return Object.values(days).every((d) => {
        if (!d || !d.open) return true;
        if (d.fullDay) return true;
        return Boolean(d.start && d.end);
      });
    },
    async load() {
      this.loading = true;
      this.loadError = '';
      try {
        const res = await this.$axios.get('/partner/availability');
        if (res?.data?.success) {
          this.availability = mergeAvailability(res.data.availability);
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          clearPartnerSession();
          this.$router.replace({ path: '/login' });
          return;
        }
        this.loadError = e?.response?.data?.message || 'Could not load availability from server.';
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (!this.validate()) {
        this.$toast?.error('Please select at least one open day with valid hours.');
        return;
      }
      this.saving = true;
      try {
        const res = await this.$axios.patch('/partner/availability', {
          availability: this.availability,
        });
        if (res?.data?.success) {
          this.availability = mergeAvailability(res.data.availability);
          this.$toast?.success('Availability saved.');
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          clearPartnerSession();
          this.$router.replace({ path: '/login' });
          return;
        }
        const errs = e?.response?.data?.errors;
        const msg = e?.response?.data?.message || 'Could not save availability.';
        if (errs && typeof errs === 'object') {
          const firstKey = Object.keys(errs)[0];
          const first = Array.isArray(errs[firstKey]) ? errs[firstKey][0] : '';
          this.$toast?.error(first || msg);
        } else {
          this.$toast?.error(msg);
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.pa-grid {
  display: grid;
  gap: 10px;
}
.pa-row {
  display: grid;
  grid-template-columns: 160px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.pa-row--off {
  opacity: 0.65;
}
.pa-day {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 13px;
}
.pa-times {
  display: flex;
  gap: 8px;
}
.pa-input {
  width: 100%;
  max-width: 150px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
}
.pa-fullday {
  font-size: 12px;
  margin: 0;
  white-space: nowrap;
}
.pa-save {
  border: none;
  border-radius: 10px;
  padding: 12px 28px;
  background: #f8721f;
  color: #fff;
}
.pa-save:disabled {
  opacity: 0.65;
}
@media (max-width: 576px) {
  .pa-row {
    grid-template-columns: 1fr;
  }
  .pa-input {
    max-width: none;
  }
}
</style>

